<script setup lang="ts">
import type { LocationClickedEvent } from '~/types/mixpanel';

import { Skeleton } from '~/components/ui/skeleton';
import { TextLink } from '~/components/ui/text-link';

interface PropertyLocationInfoProps {
	propertyType: string;
	region: string;
	city: string;
	state: string;
	isLoading?: boolean;
}

const props = withDefaults(defineProps<PropertyLocationInfoProps>(), {
	isLoading: false
});

const emit = defineEmits<{
	'location-clicked': [LocationClickedEvent];
}>();

const locationText = computed(
	() => `${props.region}, ${props.city} - ${props.state}`
);

function handleLocationClick({
	clickLink = false,
	clickMap = false
}: Partial<LocationClickedEvent>) {
	emit('location-clicked', { clickLink, clickMap });
}
</script>

<template>
	<div class="flex w-full items-center gap-3 md:gap-[14px]">
		<NuxtImg
			:src="'/images/map/location_placeholder.svg'"
			alt="Mapa da localização"
			class="size-[52px] shrink-0 rounded-sm object-cover md:size-[60px]"
			format="webp"
			@click="handleLocationClick({ clickMap: true })"
		/>

		<div class="flex min-w-0 flex-1 flex-col gap-0.5 md:gap-1 xl:items-end">
			<Skeleton :is-active="isLoading" variant="white" class="h-[26px] w-full">
				<h3
					data-test-id="details-property-title"
					class="truncate text-base font-medium leading-[26px] text-primary md:text-right lg:text-left xl:text-right xl:text-lg"
				>
					{{ propertyType }}
				</h3>
			</Skeleton>

			<Skeleton :is-active="isLoading" variant="white" class="h-5 w-full">
				<button
					class="group flex w-full flex-col items-start text-left xl:items-end xl:text-right"
					@click="handleLocationClick({ clickLink: true })"
				>
					<TextLink
						class="truncate text-sm font-normal leading-normal text-primary xl:text-base"
						as="h3"
					>
						{{ locationText }}
					</TextLink>
				</button>
			</Skeleton>
		</div>
	</div>
</template>
